import { useState, useEffect } from 'react';
import { formatCurrency } from '../lib/format';
import { Package, Plus, Edit3, Trash2, X, Save, AlertCircle } from 'lucide-react';
import { apiClient } from '../lib/api';
import { toast } from '../lib/toast';
import { AsyncSearchSelect } from '../components/common/AsyncSearchSelect';
import { normalizePagination } from '../lib/pagination';
import type { ServerListQuery } from '../hooks/useServerList';

interface Product {
  _id: string;
  displayName: string;
  basePrice: number;
  productType: 'medication' | 'supply';
}

interface Physician {
  _id: string;
  email: string;
  profile: {
    firstName: string;
    lastName: string;
  };
}

// Physicians and medications are picked by server search: there are more of
// either than one page holds. (This used to call GET /users, which does not
// exist, so the physician dropdowns were always empty.)
const fetchPhysicianPage = ({ page, pageSize, search, signal }: ServerListQuery<Record<string, never>>) =>
  apiClient.searchUsersPage({ role: 'physician', page, pageSize, search, signal });

const fetchMedicationPage = async ({ page, pageSize, search, signal }: ServerListQuery<Record<string, never>>) => {
  const res = await apiClient.getProducts({ page, limit: pageSize, search: search || undefined, productType: 'medication', signal });
  if (!res.success) throw new Error(res.error || 'Failed to load products');
  const items: Product[] = res.data?.products || [];
  const p = normalizePagination(res.data?.pagination, { page, pageSize, itemCount: items.length });
  return { items, total: p.total, hasMore: p.hasMore };
};

const physicianLabel = (p: Physician) => `${p.profile.firstName} ${p.profile.lastName}`;

interface SupplyRule {
  _id: string;
  physicianId: Physician | string;
  supplyProductId: Product | string;
  triggerProductId?: Product | string | null;
  ratio: number;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export default function SupplyRules() {
  const [rules, setRules] = useState<SupplyRule[]>([]);
  const [loading, setLoading] = useState(true);
  const [supplyProducts, setSupplyProducts] = useState<Product[]>([]);

  // Physician filter for viewing rules
  const [filterPhysician, setFilterPhysician] = useState<Physician | null>(null);
  const selectedPhysicianFilter = filterPhysician?._id || '';
  // The records behind formData's ids, so the pickers can label them.
  const [formPhysician, setFormPhysician] = useState<Physician | null>(null);
  const [formTrigger, setFormTrigger] = useState<Product | null>(null);

  // Create/Edit form state
  const [showForm, setShowForm] = useState(false);
  const [editingRule, setEditingRule] = useState<SupplyRule | null>(null);
  const [formData, setFormData] = useState({
    physicianId: '',
    supplyProductId: '',
    triggerProductId: '',
    ratio: 1
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchSupplyProducts();
  }, []);

  useEffect(() => {
    fetchRules();
  }, [selectedPhysicianFilter]);

  const fetchRules = async () => {
    try {
      setLoading(true);
      const query = selectedPhysicianFilter ? `?physicianId=${selectedPhysicianFilter}` : '';
      const res = await apiClient.get<{ success: boolean; data: SupplyRule[] }>(`/supply-rules${query}`);
      if (res.success) {
        setRules(res.data);
      }
    } catch (err) {
      toast.error('Failed to fetch supply rules');
    } finally {
      setLoading(false);
    }
  };

  // Supplies are a short list, but walk every page so none past the first is
  // dropped (the endpoint caps a page at 100).
  const fetchSupplyProducts = async () => {
    try {
      const res = await apiClient.getAllProducts(100, { productType: 'supply' });
      if (res.success && res.data?.products) {
        setSupplyProducts(res.data.products as Product[]);
      }
    } catch (err) {
      console.error('Failed to fetch products:', err);
    }
  };

  const handleCreate = () => {
    setEditingRule(null);
    setFormPhysician(filterPhysician);
    setFormTrigger(null);
    setFormData({
      physicianId: selectedPhysicianFilter || '',
      supplyProductId: '',
      triggerProductId: '',
      ratio: 1
    });
    setShowForm(true);
  };

  const handleEdit = (rule: SupplyRule) => {
    setEditingRule(rule);
    const physicianId = typeof rule.physicianId === 'object' ? rule.physicianId._id : rule.physicianId;
    const supplyId = typeof rule.supplyProductId === 'object' ? rule.supplyProductId._id : rule.supplyProductId;
    const triggerId = rule.triggerProductId
      ? (typeof rule.triggerProductId === 'object' ? rule.triggerProductId._id : rule.triggerProductId)
      : '';
    setFormPhysician(typeof rule.physicianId === 'object' ? rule.physicianId : null);
    setFormTrigger(rule.triggerProductId && typeof rule.triggerProductId === 'object' ? rule.triggerProductId : null);
    setFormData({
      physicianId,
      supplyProductId: supplyId,
      triggerProductId: triggerId,
      ratio: rule.ratio
    });
    setShowForm(true);
  };

  const handleSave = async () => {
    if (!formData.physicianId) {
      toast.warning('Please select a physician');
      return;
    }
    if (!formData.supplyProductId) {
      toast.warning('Please select a supply product');
      return;
    }

    try {
      setSaving(true);
      const payload = {
        physicianId: formData.physicianId,
        supplyProductId: formData.supplyProductId,
        triggerProductId: formData.triggerProductId || undefined,
        ratio: formData.ratio
      };

      if (editingRule) {
        const res = await apiClient.put(`/supply-rules/${editingRule._id}`, payload);
        if (res.success) {
          toast.success('Supply rule updated');
          fetchRules();
        } else {
          toast.error(res.error || 'Failed to update rule');
        }
      } else {
        const res = await apiClient.post('/supply-rules', payload);
        if (res.success) {
          toast.success('Supply rule created');
          fetchRules();
        } else {
          toast.error(res.error || 'Failed to create rule');
        }
      }
      setShowForm(false);
    } catch (err: any) {
      toast.error(err.message || 'Failed to save rule');
    } finally {
      setSaving(false);
    }
  };

  const handleDeactivate = async (rule: SupplyRule) => {
    try {
      const res = await apiClient.delete(`/supply-rules/${rule._id}`);
      if (res.success) {
        toast.success('Supply rule deactivated');
        fetchRules();
      } else {
        toast.error(res.error || 'Failed to deactivate rule');
      }
    } catch (err) {
      toast.error('Failed to deactivate rule');
    }
  };

  const getProductName = (product: Product | string): string => {
    if (typeof product === 'object' && product !== null) return product.displayName;
    return 'Unknown Product';
  };

  const getProductPrice = (product: Product | string): number => {
    if (typeof product === 'object' && product !== null) return product.basePrice;
    return 0;
  };

  const getPhysicianName = (physician: Physician | string): string => {
    if (typeof physician === 'object' && physician !== null) {
      return `${physician.profile.firstName} ${physician.profile.lastName}`;
    }
    return 'Unknown Physician';
  };

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Package className="h-7 w-7 text-blue-600" />
            Supply Rules
          </h1>
          <p className="mt-1 text-sm text-gray-600">
            Configure per-physician injection supply rules for bulk orders.
          </p>
        </div>
        <button
          onClick={handleCreate}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2"
        >
          <Plus className="w-4 h-4" />
          Add Rule
        </button>
      </div>

      {/* Info banner */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
        <div className="flex gap-3">
          <AlertCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-blue-800">
            <p className="font-medium mb-1">How Supply Rules Work</p>
            <ul className="list-disc list-inside space-y-1 text-blue-700">
              <li>Each supply rule is assigned to a <strong>specific physician</strong>. No rules assigned = no supplies added.</li>
              <li>The <strong>ratio</strong> determines how many supply units per medication unit (e.g., ratio of 2 = 2 supplies per 1 medication).</li>
              <li>If no trigger product is specified, the rule applies to <strong>all medications</strong>.</li>
              <li>Supplies use flat base price (no catalog markup) and are <strong>not</strong> sent to the pharmacy.</li>
            </ul>
          </div>
        </div>
      </div>

      {/* Physician filter */}
      <div className="mb-4">
        <label className="block text-sm font-medium text-gray-700 mb-1">Filter by Physician</label>
        <div className="max-w-md">
          <AsyncSearchSelect<Physician>
            fetchPage={fetchPhysicianPage}
            getKey={(p) => p._id}
            getLabel={physicianLabel}
            getDescription={(p) => p.email}
            value={filterPhysician}
            onChange={setFilterPhysician}
            placeholder="All physicians — search to filter"
            noun="physicians"
            aria-label="Filter by Physician"
          />
          {filterPhysician && (
            <button onClick={() => setFilterPhysician(null)} className="mt-1 text-xs text-gray-500 hover:text-gray-700">
              Show all physicians
            </button>
          )}
        </div>
      </div>

      {/* Rules Table */}
      {loading ? (
        <div className="flex items-center justify-center h-40">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
        </div>
      ) : rules.length === 0 ? (
        <div className="bg-white rounded-lg shadow-sm border p-12 text-center">
          <Package className="w-12 h-12 text-gray-400 mx-auto mb-3" />
          <p className="text-gray-500 mb-4">
            {selectedPhysicianFilter
              ? 'No supply rules for this physician.'
              : 'No supply rules configured yet.'}
          </p>
          <button
            onClick={handleCreate}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 inline-flex items-center gap-2"
          >
            <Plus className="w-4 h-4" />
            Create First Rule
          </button>
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow-sm border overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                {!selectedPhysicianFilter && (
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Physician</th>
                )}
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Supply Product</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Trigger</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Ratio</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Price</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {rules.map(rule => (
                <tr key={rule._id} className={!rule.isActive ? 'bg-gray-50 opacity-60' : ''}>
                  {!selectedPhysicianFilter && (
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">{getPhysicianName(rule.physicianId)}</div>
                    </td>
                  )}
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="font-medium text-gray-900">{getProductName(rule.supplyProductId)}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {rule.triggerProductId ? (
                      <span className="text-gray-900">{getProductName(rule.triggerProductId)}</span>
                    ) : (
                      <span className="text-gray-400 italic">All medications</span>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="text-gray-900">{rule.ratio}:1</span>
                    <span className="text-xs text-gray-500 ml-1">per medication unit</span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="text-gray-900">
                      {getProductPrice(rule.supplyProductId) === 0
                        ? <span className="text-green-600 font-medium">Free</span>
                        : formatCurrency(getProductPrice(rule.supplyProductId))
                      }
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                      rule.isActive ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'
                    }`}>
                      {rule.isActive ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right">
                    <div className="flex items-center justify-end gap-2">
                      <button
                        onClick={() => handleEdit(rule)}
                        className="p-1.5 text-gray-400 hover:text-blue-600 rounded"
                        title="Edit rule"
                      >
                        <Edit3 className="w-4 h-4" />
                      </button>
                      {rule.isActive && (
                        <button
                          onClick={() => handleDeactivate(rule)}
                          className="p-1.5 text-gray-400 hover:text-red-600 rounded"
                          title="Deactivate rule"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Create/Edit Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl max-w-lg w-full">
            <div className="p-6 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h2 className="text-lg font-semibold text-gray-900">
                  {editingRule ? 'Edit Supply Rule' : 'Create Supply Rule'}
                </h2>
                <button onClick={() => setShowForm(false)} className="text-gray-400 hover:text-gray-600">
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>

            <div className="p-6 space-y-4">
              {/* Physician */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Physician *</label>
                <AsyncSearchSelect<Physician>
                  fetchPage={fetchPhysicianPage}
                  getKey={(p) => p._id}
                  getLabel={physicianLabel}
                  getDescription={(p) => p.email}
                  value={formPhysician}
                  onChange={(p) => { setFormPhysician(p); setFormData(prev => ({ ...prev, physicianId: p?._id || '' })); }}
                  placeholder="Search physicians..."
                  noun="physicians"
                  disabled={!!editingRule}
                  aria-label="Physician"
                />
              </div>

              {/* Supply Product */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Supply Product</label>
                <select
                  value={formData.supplyProductId}
                  onChange={(e) => setFormData(prev => ({ ...prev, supplyProductId: e.target.value }))}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="">Select supply product...</option>
                  {supplyProducts.map(p => (
                    <option key={p._id} value={p._id}>
                      {p.displayName} ({p.basePrice === 0 ? 'Free' : formatCurrency(p.basePrice)})
                    </option>
                  ))}
                </select>
                {supplyProducts.length === 0 && (
                  <p className="text-xs text-amber-600 mt-1">
                    No supply products found. Create a product with type &quot;Supply&quot; first.
                  </p>
                )}
              </div>

              {/* Trigger Product */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Trigger Product (Optional)</label>
                <AsyncSearchSelect<Product>
                  fetchPage={fetchMedicationPage}
                  getKey={(p) => p._id}
                  getLabel={(p) => p.displayName}
                  value={formTrigger}
                  onChange={(p) => { setFormTrigger(p); setFormData(prev => ({ ...prev, triggerProductId: p?._id || '' })); }}
                  placeholder="All medications (wildcard) — search to pick one"
                  noun="medications"
                  aria-label="Trigger Product"
                />
                {formTrigger && (
                  <button
                    onClick={() => { setFormTrigger(null); setFormData(prev => ({ ...prev, triggerProductId: '' })); }}
                    className="mt-1 text-xs text-gray-500 hover:text-gray-700"
                  >
                    Use all medications instead
                  </button>
                )}
                <p className="text-xs text-gray-500 mt-1">
                  Leave empty to trigger for any medication in the order.
                </p>
              </div>

              {/* Ratio */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Ratio (supply units per medication unit)
                </label>
                <input
                  type="number"
                  min="0.01"
                  step="0.01"
                  defaultValue={formData.ratio}
                  key={`ratio-${formData.ratio}`}
                  onChange={(e) => { const v = parseFloat(e.target.value); if (!isNaN(v)) setFormData(prev => ({ ...prev, ratio: v })); }}
                  onBlur={(e) => { const v = parseFloat(e.target.value); const c = isNaN(v) || v < 0.01 ? 1 : v; setFormData(prev => ({ ...prev, ratio: c })); e.target.value = String(c); }}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-blue-500 focus:border-blue-500"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Example: ratio of 2 means for every 1 medication unit, 2 supply units are added.
                </p>
              </div>
            </div>

            <div className="p-6 border-t border-gray-200 flex items-center justify-end gap-3">
              <button
                onClick={() => setShowForm(false)}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                disabled={saving || !formData.supplyProductId || !formData.physicianId}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2"
              >
                {saving ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Save className="w-4 h-4" />
                    {editingRule ? 'Update Rule' : 'Create Rule'}
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
